import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Edit3, Heart, Minus, Plus, Send, ShoppingCart, Star, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../api/client";
import { FILTERS, filterProductMeta } from "../components/FilterDrawer";
import { useAuth } from "../context/AuthContext";
import { useShop } from "../context/ShopContext";
import useScrollReveal from "../hooks/useScrollReveal";

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { products, loading, addToCart, toggleFavorite, favorites, getDisplayPrice } = useShop();
  const { user, isAdmin } = useAuth();

  const p = products.find((item) => String(item.id) === String(id));
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [reviewState, setReviewState] = useState({ reviews: [], summary: { count: 0, average: 0 }, canReview: false, ownReview: null });
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });
  const [reviewError, setReviewError] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [deletingReviewId, setDeletingReviewId] = useState(null);

  useScrollReveal();

  useEffect(() => {
    if (!id) return;
    let active = true;
    api(`/products/${id}/reviews`)
      .then((data) => {
        if (!active) return;
        setReviewState(data);
        if (data.ownReview) setReviewForm({ rating: data.ownReview.rating, comment: data.ownReview.comment });
      })
      .catch(() => {});
    return () => { active = false; };
  }, [id, user?.id]);

  if (loading) return <div className="px-8 py-16 text-stone-600">Ürün yükleniyor...</div>;

  if (!p) {
    return (
      <div className="px-8 py-16">
        <h1 className="text-2xl font-bold">Ürün bulunamadı</h1>
        <Link to="/" className="mt-4 inline-block text-stone-600 underline">
          Anasayfaya dön
        </Link>
      </div>
    );
  }

  const price = getDisplayPrice(p);
  const stock = Number(p.stock || 0);
  const images = p.images?.length ? p.images : [p.image].filter(Boolean);
  const hasMultipleImages = images.length > 1;
  const meta = filterProductMeta(p);

  const detailRows = [
    { label: "Ürün Kodu", value: p.code || p.id },
    { label: "Bıçak Türü", value: p.type },
    { label: "Kullanım Alanı", value: p.usageArea },
    { label: "Stok", value: stock },
    { label: "Bireysel Fiyat", value: `₺ ${Number(p.price || 0).toLocaleString("tr-TR")}` },
    { label: "Kurumsal Fiyat", value: `₺ ${Number(p.corporatePrice || p.price || 0).toLocaleString("tr-TR")}` },
    ...FILTERS.filter((filter) => filter.key !== "type").map((filter) => ({
      label: filter.label,
      value: meta[filter.key] || p[filter.key],
    })),
  ];

  const increase = () => {
    if (quantity < stock) setQuantity(quantity + 1);
  };

  const decrease = () => {
    if (quantity > 1) setQuantity(quantity - 1);
  };

  const showPreviousImage = () => {
    setSelectedImage((current) => (current === 0 ? images.length - 1 : current - 1));
  };

  const showNextImage = () => {
    setSelectedImage((current) => (current + 1) % images.length);
  };

  const submitReview = async () => {
    try {
      setReviewBusy(true);
      setReviewError("");
      await api(`/products/${p.id}/reviews`, { method: "POST", body: JSON.stringify(reviewForm) });
      const data = await api(`/products/${p.id}/reviews`);
      setReviewState(data);
      if (data.ownReview) setReviewForm({ rating: data.ownReview.rating, comment: data.ownReview.comment });
    } catch (error) {
      setReviewError(error.message);
    } finally {
      setReviewBusy(false);
    }
  };

  const editReview = (review) => {
    setReviewForm({ rating: Number(review.rating || 5), comment: review.comment || "" });
    document.getElementById("review-form")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const deleteReview = async (review) => {
    if (!window.confirm("Bu yorum silinsin mi?")) return;
    try {
      setDeletingReviewId(review.id);
      setReviewError("");
      await api(`/products/${p.id}/reviews/${review.id}`, { method: "DELETE" });
      const data = await api(`/products/${p.id}/reviews`);
      setReviewState(data);
      if (Number(review.userId) === Number(user?.id)) setReviewForm({ rating: 5, comment: "" });
    } catch (error) {
      setReviewError(error.message);
    } finally {
      setDeletingReviewId(null);
    }
  };

  return (
    <section className="reveal mx-auto w-full max-w-7xl overflow-hidden px-4 py-8 sm:px-5 md:px-10">
      <button
        onClick={() => navigate(-1)}
        className="mb-6 flex items-center gap-2 text-sm font-medium text-stone-500 transition hover:text-stone-950"
      >
        <ArrowLeft size={18} />
        Geri Dön
      </button>

      <div className="grid min-w-0 items-start gap-8 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] lg:gap-10">
        <div className="min-w-0">
          <div className="min-w-0 lg:sticky lg:top-6">
            <div className="product-image-zoom-wrap relative w-full overflow-hidden bg-stone-100">
              <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${selectedImage * 100}%)` }}>
                {images.map((img, index) => (
                  <div key={`${img}-${index}`} className="min-w-full">
                    <img
                      src={img}
                      alt={`${p.name} ${index + 1}`}
                      className="product-image-zoom aspect-[4/5] h-auto max-h-[560px] w-full object-contain sm:aspect-[5/6]"
                    />
                  </div>
                ))}
              </div>

              {hasMultipleImages && (
                <>
                  <button
                    type="button"
                    onClick={showPreviousImage}
                    aria-label="Önceki görsel"
                    className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-stone-200 bg-white/90 text-stone-950 shadow-sm hover:bg-white sm:left-4 sm:h-11 sm:w-11"
                  >
                    <ChevronLeft size={22} />
                  </button>

                  <button
                    type="button"
                    onClick={showNextImage}
                    aria-label="Sonraki görsel"
                    className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-stone-200 bg-white/90 text-stone-950 shadow-sm hover:bg-white sm:right-4 sm:h-11 sm:w-11"
                  >
                    <ChevronRight size={22} />
                  </button>

                  <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2 rounded-full bg-white/85 px-3 py-2 shadow-sm">
                    {images.map((img, index) => (
                      <button
                        key={`dot-${img}-${index}`}
                        type="button"
                        onClick={() => setSelectedImage(index)}
                        aria-label={`${index + 1}. görsele geç`}
                        className={`h-2.5 w-2.5 rounded-full ${selectedImage === index ? "bg-stone-950" : "bg-stone-300"}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>

            {hasMultipleImages && (
              <div className="mt-4 flex max-w-full gap-3 overflow-x-auto pb-2">
                {images.map((img, index) => (
                  <button
                    key={`${img}-${index}`}
                    type="button"
                    onClick={() => setSelectedImage(index)}
                    className={`h-20 w-20 shrink-0 overflow-hidden border bg-stone-100 transition ${selectedImage === index ? "border-stone-950" : "border-stone-200 opacity-70 hover:opacity-100"}`}
                  >
                    <img src={img} alt={p.name} className="h-full w-full object-contain" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex min-w-0 flex-col lg:min-h-[560px]">
          <div className="mb-4 flex items-center gap-3 border-b border-stone-200 pb-4 text-sm">
            {!isAdmin && (
              <button
                onClick={() => toggleFavorite(p.id)}
                className={`flex items-center gap-2 border-b-2 pb-3 transition ${favorites.includes(p.id) ? "border-red-500 text-red-500" : "border-transparent text-stone-500 hover:text-stone-950"}`}
              >
                <Heart size={17} fill={favorites.includes(p.id) ? "currentColor" : "none"} />
                Favori
              </button>
            )}

            <span className="ml-auto rounded-full bg-stone-100 px-3 py-1 text-xs font-semibold text-stone-600">
              {stock > 0 ? `${stock} adet stokta` : "Stok yok"}
            </span>
          </div>

          <p className="mb-2 text-sm font-medium text-stone-400">{p.code || p.id}</p>

          <h1 className="text-3xl font-bold leading-tight text-stone-950 md:text-4xl">{p.name}</h1>

          <p className="mt-3 text-sm font-medium text-stone-500">
            {p.type} {p.usageArea ? `• ${p.usageArea}` : ""}
          </p>

          <div className="mt-5">
            <p className="text-3xl font-extrabold tracking-tight text-stone-950">
              ₺ {Number(price).toLocaleString("tr-TR")}
            </p>
          </div>

          {p.description && <p className="mt-6 break-words leading-7 text-stone-600">{p.description}</p>}

          {!isAdmin && (
            <div className="mt-7 grid gap-3 sm:grid-cols-[140px_1fr]">
              <div className="flex h-14 items-center border border-stone-200">
                <button onClick={decrease} className="flex h-full w-12 items-center justify-center">
                  <Minus size={16} />
                </button>

                <span className="flex-1 text-center font-semibold">{quantity}</span>

                <button onClick={increase} disabled={quantity >= stock} className="flex h-full w-12 items-center justify-center disabled:text-stone-300">
                  <Plus size={16} />
                </button>
              </div>

              <button
                disabled={stock <= 0}
                onClick={() => addToCart(p, quantity)}
                className="flex h-14 items-center justify-center gap-2 bg-stone-950 px-6 font-bold text-white transition hover:bg-stone-700 disabled:bg-stone-300"
              >
                <ShoppingCart size={19} />
                {stock <= 0 ? "Stok Yok" : "Sepete Ekle"}
              </button>
            </div>
          )}

          <div className="mt-auto pt-8">
            <div className="border-t border-stone-200">
              <h2 className="py-4 text-base font-bold text-stone-950">Teknik Özellikler</h2>

              <div className="divide-y divide-stone-100 border-t border-stone-100">
                {detailRows.map((row) => (
                  <div key={row.label} className="grid min-w-0 grid-cols-2 gap-4 py-3 text-sm">
                    <span className="text-stone-500">{row.label}</span>
                    <span className="break-words text-right font-semibold text-stone-950">{row.value || "Belirtilmedi"}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 border-t border-stone-200 pt-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-stone-950">Ürün Yorumları</h2>
            <p className="mt-1 text-sm text-stone-500">
              {reviewState.summary.count
                ? `${reviewState.summary.average.toFixed(1)} / 5 puan, ${reviewState.summary.count} yorum`
                : "Bu ürün için henüz yorum yapılmamış."}
            </p>
          </div>
          <div className="flex items-center gap-1 text-amber-500">
            {[1, 2, 3, 4, 5].map((value) => (
              <Star key={value} size={20} fill={value <= Math.round(reviewState.summary.average) ? "currentColor" : "none"} />
            ))}
          </div>
        </div>

        {!isAdmin && (
          <div id="review-form" className="mt-6 rounded-2xl border border-stone-200 bg-white p-4 sm:p-5">
            {reviewState.canReview ? (
              <>
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <span className="text-sm font-semibold text-stone-700">
                    {reviewState.ownReview ? "Yorumunu güncelle" : "Satın aldığın ürün için yorum yaz"}
                  </span>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((value) => (
                      <button key={value} type="button" onClick={() => setReviewForm({ ...reviewForm, rating: value })} className="text-amber-500">
                        <Star size={22} fill={value <= reviewForm.rating ? "currentColor" : "none"} />
                      </button>
                    ))}
                  </div>
                </div>
                {reviewError && <div className="mb-3 rounded-xl bg-red-50 p-3 text-sm text-red-600">{reviewError}</div>}
                <textarea
                  value={reviewForm.comment}
                  onChange={(event) => setReviewForm({ ...reviewForm, comment: event.target.value })}
                  maxLength={1000}
                  rows="3"
                  placeholder="Ürün deneyimini paylaş"
                  className="w-full rounded-xl border bg-white p-3"
                />
                <div className="mt-3 flex flex-wrap gap-2">
                  <button onClick={submitReview} disabled={reviewBusy || reviewForm.comment.trim().length < 5} className="flex items-center gap-2 rounded-xl bg-stone-950 px-5 py-3 text-sm font-bold text-white disabled:opacity-50">
                    <Send size={17} />
                    {reviewBusy ? "Gönderiliyor..." : reviewState.ownReview ? "Yorumu Güncelle" : "Yorumu Gönder"}
                  </button>
                </div>
              </>
            ) : (
              <p className="text-sm text-stone-500">
                Yorum yapabilmek için bu ürünü satın almış ve hesabınla giriş yapmış olman gerekir.
              </p>
            )}
          </div>
        )}

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {reviewState.reviews.length ? reviewState.reviews.map((review) => (
            <div key={review.id} className="flex min-h-[210px] flex-col rounded-lg border border-stone-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="truncate text-sm font-bold text-stone-950">{review.userName}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] font-semibold text-stone-500">
                    <span className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-700">Onaylı alışveriş</span>
                    <span>{new Date(review.updatedAt || review.createdAt).toLocaleDateString("tr-TR")}</span>
                  </div>
                </div>
                <div className="flex shrink-0 gap-0.5 rounded-full bg-amber-50 px-2 py-1 text-amber-500">
                  {[1, 2, 3, 4, 5].map((value) => (
                    <Star key={value} size={14} fill={value <= Number(review.rating) ? "currentColor" : "none"} />
                  ))}
                </div>
              </div>
              <p className="mt-4 flex-1 rounded-lg bg-stone-50 p-3 text-sm leading-6 text-stone-700">{review.comment}</p>
              {(Number(review.userId) === Number(user?.id) || isAdmin) && (
                <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-stone-100 pt-3">
                  {Number(review.userId) === Number(user?.id) && !isAdmin && (
                    <button onClick={() => editReview(review)} className="flex items-center gap-1.5 rounded-md border border-stone-200 px-3 py-2 text-xs font-bold text-stone-700 hover:border-stone-400 hover:bg-stone-50">
                      <Edit3 size={14} />
                      Düzenle
                    </button>
                  )}
                  <button onClick={() => deleteReview(review)} disabled={deletingReviewId === review.id} className="flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 disabled:opacity-50">
                    <Trash2 size={14} />
                    {deletingReviewId === review.id ? "Siliniyor..." : "Sil"}
                  </button>
                </div>
              )}
            </div>
          )) : (
            <div className="rounded-2xl border border-stone-200 bg-white p-5 text-sm text-stone-500 sm:col-span-2 lg:col-span-3">
              İlk yorum, ürünü satın alan müşteriden sonra burada görünecek.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
